import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Flex, Text } from '@bonusly/design-system';
import { RefreshOutline } from '@bonusly/ds-icon';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { versionTelemetry } from '../../lib/version-telemetry';

const currentVersion = import.meta.env.REACT_APP_GIT_SHA || 'local';

/**
 * Banner displayed when the frontend detects it's running an outdated version.
 * Checks /version.json when the tab becomes visible and prompts user to refresh if outdated.
 *
 * Local testing: create public/version.json with {"sha": "test"}, then switch tabs.
 */
export const VersionBanner = () => {
	const { t } = useTranslation('common');
	const [expectedVersion, setExpectedVersion] = useState<string | null>(null);
	const currentUser = useCurrentUser();
	const currentCompany = useCurrentCompany();
	const hasLoggedShown = useRef(false);

	const isOutdated = expectedVersion !== null && expectedVersion !== currentVersion;

	// Check version when tab becomes visible
	useEffect(() => {
		const checkVersion = async () => {
			try {
				const response = await fetch(`/version.json?t=${Date.now()}`);
				if (!response.ok) return;

				const { sha } = await response.json();
				if (sha && sha !== currentVersion) {
					setExpectedVersion(sha);
				}
			} catch {
				// Silently fail - version check is non-critical
			}
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				checkVersion();
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, []);

	// Log when banner is shown (only once per session)
	useEffect(() => {
		if (isOutdated && !hasLoggedShown.current && currentUser && currentCompany && expectedVersion) {
			hasLoggedShown.current = true;
			versionTelemetry.bannerShown(currentUser.id, currentCompany.id, currentVersion, expectedVersion);
		}
	}, [isOutdated, currentUser, currentCompany, expectedVersion]);

	if (!isOutdated) {
		return null;
	}

	const handleRefresh = () => {
		if (currentUser && currentCompany && expectedVersion) {
			versionTelemetry.refreshClicked(currentUser.id, currentCompany.id, currentVersion, expectedVersion);
		}
		window.location.reload();
	};

	return (
		<Box position="fixed" top={0} left={0} right={0} zIndex="banner" bg="primary.bg" py={3} px={4} shadow="md">
			<Flex alignItems="center" justifyContent="center" gap={3}>
				<RefreshOutline color="text.onColor" />
				<Text color="text.onColor" fontWeight="semibold">
					{t('version_banner.title', 'A new version of Bonusly is available')}
				</Text>
				<Button size="sm" variant="outline" colorScheme="secondary" onClick={handleRefresh}>
					{t('version_banner.refresh', 'Refresh')}
				</Button>
			</Flex>
		</Box>
	);
};
